import { coerceSnapshotForLegacyImport } from "../../emitter_tool/utils/PresetApply";

let spellEnvironment = {
  runtime: null,
  app: null,
  getImageSize: () => [1920, 1080],
  gridConfig: {},
  getWallPolygons: null,
  wallPolygons: null,
};

export function registerSpellRuntime({
  runtime,
  app,
  getImageSize,
  gridConfig,
  getWallPolygons,
  wallPolygons,
}) {
  spellEnvironment = {
    runtime: runtime || null,
    app: app || null,
    getImageSize:
      typeof getImageSize === "function" ? getImageSize : () => [1920, 1080],
    gridConfig: gridConfig || {},
    getWallPolygons:
      typeof getWallPolygons === "function" ? getWallPolygons : null,
    wallPolygons: wallPolygons || null,
  };
}

const MANUAL_SPELLS = [
  ["fire_cone", "Burning Hands"],
  ["Bouncing_ball", "Fire Missile"],
];

const ensureArray = (a) => (Array.isArray(a) ? a : []);
const deepClone = (v) => JSON.parse(JSON.stringify(v ?? null));

function mergePanelIntoEffectSettings(target, panel, panelType) {
  if (!panel || typeof panel !== "object") return target;
  if (!target || typeof target !== "object") target = {};

  const out = { ...target };
  const entries = Object.entries(panel);

  for (const [rawKey, value] of entries) {
    let key = rawKey;
    if (panelType === "SpritePanel") {
      if (key === "mode") key = "spriteMode";
    }
    out[key] = value;
  }

  return out;
}

function buildRuntimePresetFromSpellPreset(spellPreset, pxPerMeterOverride) {
  if (!spellPreset || typeof spellPreset !== "object") return null;

  let preset = deepClone(spellPreset);

  try {
    const coerced = coerceSnapshotForLegacyImport(preset);
    if (coerced && typeof coerced === "object") {
      preset = coerced;
    }
  } catch (e) {}

  if (
    preset.parent &&
    typeof preset.parent === "object" &&
    Array.isArray(preset.layers)
  ) {
    const out = deepClone(preset);

    if (!out.parent || typeof out.parent !== "object") {
      out.parent = {};
    }
    if (out.parent.emitterShape == null) {
      out.parent.emitterShape = "dot";
    }

    if (Number.isFinite(pxPerMeterOverride)) {
      out.pxPerMeter = pxPerMeterOverride;
    } else if (!Number.isFinite(out.pxPerMeter)) {
      out.pxPerMeter = undefined;
    }

    return out;
  }

  const emitterPanel = preset.EmitterPanel || {};
  const emitterPanelShape = emitterPanel.shape;
  const emitterPanelSettings =
    (emitterPanel && emitterPanel.settings) || undefined;

  const parentBlock = preset.parent || {};

  const parentShapeRaw =
    emitterPanelShape != null
      ? emitterPanelShape
      : parentBlock.emitterShape != null
      ? parentBlock.emitterShape
      : preset.emitterShape != null
      ? preset.emitterShape
      : null;

  const parentShape = parentShapeRaw != null ? parentShapeRaw : "dot";

  let parentShapeSettings =
    deepClone(
      parentBlock.shapeSettings != null
        ? parentBlock.shapeSettings
        : preset.shapeSettings != null
        ? preset.shapeSettings
        : {}
    ) || {};

  if (emitterPanelSettings && typeof emitterPanelSettings === "object") {
    parentShapeSettings = {
      ...parentShapeSettings,
      ...deepClone(emitterPanelSettings),
    };
  }

  let parentES =
    deepClone(
      parentBlock.effectSettings != null
        ? parentBlock.effectSettings
        : preset.effectSettings != null
        ? preset.effectSettings
        : {}
    ) || {};

  const spritePanel = preset.SpritePanel || null;
  const particlesPanel = preset.ParticlesPanel || null;
  const glowPanel = preset.GlowPanel || null;
  const collisionPanel = preset.CollisionPanel || null;
  const orbitPanel = preset.OrbitPanel || null;
  const rotationPanel = preset.RotationPanel || null;
  const gravityPanel = preset.GravityPanel || null;

  parentES = mergePanelIntoEffectSettings(parentES, spritePanel, "SpritePanel");
  parentES = mergePanelIntoEffectSettings(
    parentES,
    particlesPanel,
    "ParticlesPanel"
  );
  parentES = mergePanelIntoEffectSettings(parentES, glowPanel, "GlowPanel");
  parentES = mergePanelIntoEffectSettings(
    parentES,
    collisionPanel,
    "CollisionPanel"
  );
  parentES = mergePanelIntoEffectSettings(parentES, orbitPanel, "OrbitPanel");
  parentES = mergePanelIntoEffectSettings(
    parentES,
    rotationPanel,
    "RotationPanel"
  );
  parentES = mergePanelIntoEffectSettings(
    parentES,
    gravityPanel,
    "GravityPanel"
  );

  const selectedSprite =
    preset.selectedSprite != null
      ? preset.selectedSprite
      : parentES.selectedSprite != null
      ? parentES.selectedSprite
      : spritePanel && spritePanel.selectedSprite != null
      ? spritePanel.selectedSprite
      : null;

  if (selectedSprite != null && !parentES.selectedSprite) {
    parentES.selectedSprite = selectedSprite;
  }

  const baseLayers = Array.isArray(preset.layers) ? preset.layers : null;
  const rawSubs = ensureArray(
    (parentES && parentES.subEmitters) || preset.subEmitters
  );

  let layersOut = [];

  function buildLayerFromSubEmitter(sub) {
    const ov = sub && sub.overrides ? sub.overrides : {};

    const layerEmitterShape =
      ov.emitterShape != null ? ov.emitterShape : parentShape;

    const layerShapeSettings =
      ov.shapeSettings != null
        ? deepClone(ov.shapeSettings)
        : deepClone(parentShapeSettings) || {};

    const layerEffectSettings =
      ov.effectSettings != null
        ? deepClone(ov.effectSettings)
        : deepClone(parentES) || {};

    const selOv = {};
    if (ov.selectedSprite != null) selOv.selectedSprite = ov.selectedSprite;
    if (Array.isArray(ov.selectedSprites)) {
      selOv.selectedSprites = deepClone(ov.selectedSprites);
    }
    if (Array.isArray(ov.selectedAnimFrames)) {
      selOv.selectedAnimFrames = deepClone(ov.selectedAnimFrames);
    }
    if (ov.animFps != null) selOv.animFps = ov.animFps;

    const outLayer = {
      id: sub.id || "",
      enabled: sub.enabled !== false,
      startMode: sub.startMode || "immediate",
      delayMs: Number.isFinite(+sub.delayMs) ? +sub.delayMs : 0,
      runForMode: sub.runForMode || "inherit",
      runDurationS: Number.isFinite(+sub.runDurationS)
        ? +sub.runDurationS
        : 0.5,
      aboveParent: true,
      emitterShape: layerEmitterShape,
      shapeSettings: layerShapeSettings,
      effectSettings: layerEffectSettings,
      __selectionOverrides:
        Object.keys(selOv).length > 0 ? selOv : null,
    };

    return outLayer;
  }

  if (baseLayers && baseLayers.length > 0) {
    layersOut = deepClone(baseLayers);
  } else if (rawSubs.length > 0) {
    let parentIndexRaw =
      parentES && Number.isFinite(parentES.subEmittersParentIndex)
        ? parentES.subEmittersParentIndex
        : Number.isFinite(preset.subEmittersParentIndex)
        ? preset.subEmittersParentIndex
        : 0;
    parentIndexRaw = Number.isFinite(parentIndexRaw) ? parentIndexRaw : 0;

    const bakedChildren = rawSubs.map((sub) => buildLayerFromSubEmitter(sub));

    const visualStack = [];
    const clampedParentIndex = Math.min(
      Math.max(0, Math.floor(parentIndexRaw)),
      bakedChildren.length
    );

    for (let i = 0; i < bakedChildren.length; i++) {
      if (i === clampedParentIndex) {
        visualStack.push("__PARENT__");
      }
      visualStack.push(bakedChildren[i]);
    }
    if (clampedParentIndex >= bakedChildren.length) {
      visualStack.push("__PARENT__");
    }

    const finalLayers = [];
    let passedParent = false;
    for (let i = 0; i < visualStack.length; i++) {
      const entry = visualStack[i];
      if (entry === "__PARENT__") {
        passedParent = true;
        continue;
      }
      const layer = entry;
      layer.aboveParent = passedParent;
      finalLayers.push(layer);
    }

    layersOut = finalLayers;
  } else {
    layersOut = [];
  }

  const pxPerMeter =
    Number.isFinite(pxPerMeterOverride)
      ? pxPerMeterOverride
      : Number.isFinite(preset.pxPerMeter)
      ? preset.pxPerMeter
      : undefined;

  let rule = {};
  if (preset.rule && typeof preset.rule === "object") {
    rule = deepClone(preset.rule);
  }
  if (emitterPanel && emitterPanel.rule && typeof emitterPanel.rule === "object") {
    rule = deepClone(emitterPanel.rule);
  }

  const runtimePreset = {
    parent: {
      emitterShape: parentShape,
      shapeSettings: parentShapeSettings,
      effectSettings: parentES,
    },
    layers: layersOut,
    pxPerMeter,
    rule,
  };

  return runtimePreset;
}


function applyCollisionContextToRuntimePreset(runtimePreset, extra) {
  if (!runtimePreset || typeof runtimePreset !== "object") return runtimePreset;
  const ignoreCollisionIds = Array.isArray(extra?.ignoreCollisionIds)
    ? extra.ignoreCollisionIds
        .map((id) => (id == null ? null : String(id)))
        .filter(Boolean)
    : [];
  const sourceTokenId = extra?.sourceTokenId != null ? String(extra.sourceTokenId) : null;
  const sourceTokenType = extra?.sourceTokenType ? String(extra.sourceTokenType).toLowerCase() : null;

  if (!ignoreCollisionIds.length && !sourceTokenId && !sourceTokenType) {
    return runtimePreset;
  }

  const applyToEffectSettings = (effectSettings) => {
    const next = effectSettings && typeof effectSettings === "object" ? { ...effectSettings } : {};
    if (ignoreCollisionIds.length) next.ignoreCollisionIds = ignoreCollisionIds.slice();
    if (sourceTokenId) next.sourceTokenId = sourceTokenId;
    if (sourceTokenType) next.sourceTokenType = sourceTokenType;
    return next;
  };

  if (runtimePreset.parent && typeof runtimePreset.parent === "object") {
    runtimePreset.parent = {
      ...runtimePreset.parent,
      effectSettings: applyToEffectSettings(runtimePreset.parent.effectSettings),
    };
  }

  if (Array.isArray(runtimePreset.layers)) {
    runtimePreset.layers = runtimePreset.layers.map((layer) => {
      if (!layer || typeof layer !== "object") return layer;
      return {
        ...layer,
        effectSettings: applyToEffectSettings(layer.effectSettings),
      };
    });
  }

  return runtimePreset;
}

function normalizeSpell(entry) {
  if (!entry || !entry.mod) return null;
  const rawMod = entry.mod;
  const base =
    rawMod.default ||
    rawMod.spell ||
    rawMod.preset ||
    rawMod.effect ||
    rawMod;
  if (!base) return null;

  const id = entry.id || base.id || "spell";
  const name = entry.name || base.name || "Spell";

  return {
    id,
    name,
    description: base.description || "",
    icon: base.icon || null,
    category: base.category || null,
    order: typeof base.order === "number" ? base.order : 0,
    rules: base.rules || {},
    run:
      typeof base.cast === "function"
        ? base.cast
        : typeof base.run === "function"
        ? base.run
        : null,
    preset: base.preset || base.effect || base,
    _module: rawMod,
    _key: id,
  };
}

let SPELLS = [];
let SPELLS_BY_ID = {};

(function buildSpellRegistry() {
  const list = [];
  const byId = {};

  MANUAL_SPELLS.forEach(([id, name]) => {
    try {
      const mod = require(`./spells/${id}.js`);
      const spell = normalizeSpell({ id, name, mod });
      if (!spell || !spell.id || byId[spell.id]) return;
      byId[spell.id] = spell;
      list.push(spell);
    } catch (e) {}
  });

  list.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });

  SPELLS = list;
  SPELLS_BY_ID = byId;
})();

export function listSpells() {
  return SPELLS.slice();
}

export function getSpell(id) {
  return SPELLS_BY_ID[id] || null;
}

function applyAngleToPreset(obj, angleDeg) {
  if (!obj || typeof obj !== "object") return;

  const keys = Object.keys(obj);
  for (const k of keys) {
    const v = obj[k];

    if (
      typeof v === "number" &&
      (k === "angleDeg" ||
        k === "rotationDeg" ||
        k === "beamAngleDeg" ||
        k === "arcRotationDeg" ||
        k === "headAngleDeg" ||
        k === "facingDegFromOrigin")
    ) {
      obj[k] = angleDeg;
    } else if (v && typeof v === "object") {
      applyAngleToPreset(v, angleDeg);
    }
  }
}

export function castSpellById(spellId, options = {}) {
  const spell = getSpell(spellId);
  if (!spell) {
    return null;
  }

  const runtime = options.runtime || spellEnvironment.runtime;
  const app = options.app || spellEnvironment.app;
  const getImageSize = options.getImageSize || spellEnvironment.getImageSize;
  const gridConfig = options.gridConfig || spellEnvironment.gridConfig || {};

  if (!runtime || !app) {
    return null;
  }

  const imageSize = getImageSize() || [1920, 1080];
  const imgW = Number(imageSize[0]) || 1920;
  const imgH = Number(imageSize[1]) || 1080;

  const ppm =
    gridConfig.metre_in_pixels ||
    gridConfig.meter_in_pixels ||
    options.pxPerMeter ||
    80;

  const getWalls = spellEnvironment.getWallPolygons;
  const envWalls = spellEnvironment.wallPolygons;

  let wallsFromOptions = options.wallPolygons;

  if (!wallsFromOptions && typeof window !== "undefined") {
    try {
      if (window.__PA_WALLS) {
        wallsFromOptions = window.__PA_WALLS;
      }
    } catch {}
  }

  const wallPolygons =
    wallsFromOptions ||
    (typeof getWalls === "function" ? getWalls() : null) ||
    envWalls ||
    null;

  const hasWalls =
    !!wallPolygons &&
    ((Array.isArray(wallPolygons) && wallPolygons.length > 0) ||
      (typeof wallPolygons === "object" && wallPolygons !== null));

  if (runtime && typeof runtime.setEnvironment === "function") {
    try {
      runtime.setEnvironment({
        pxPerMeter: ppm,
        gridConfig,
        wallPolygons: wallPolygons || null,
        wallSpace: "image",
        wallDpr: 1,
        isVTT: true,
      });
    } catch (e) {}
  }

  let originOpt = options.origin || null;
  try {
    if (
      (!originOpt || !Number.isFinite(originOpt.x) || !Number.isFinite(originOpt.y)) &&
      typeof window !== "undefined"
    ) {
      const g = window.__PA_SPELL_ORIGIN;
      if (g && Number.isFinite(g.x) && Number.isFinite(g.y)) {
        originOpt = { x: g.x, y: g.y };
      }
    }
  } catch {}

  let targetOpt = options.target || null;

  const ox = originOpt != null ? Number(originOpt.x) : NaN;
  const oy = originOpt != null ? Number(originOpt.y) : NaN;
  const hasOrigin = Number.isFinite(ox) && Number.isFinite(oy);

  const tx = targetOpt != null ? Number(targetOpt.x) : NaN;
  const ty = targetOpt != null ? Number(targetOpt.y) : NaN;
  const hasTarget = Number.isFinite(tx) && Number.isFinite(ty);

  let directionAngleDeg = null;
  if (hasOrigin && hasTarget) {
    const dx = tx - ox;
    const dy = ty - oy;
    const angleRad = Math.atan2(dy, dx);
    let deg = (angleRad * 180) / Math.PI;
    if (!Number.isNaN(deg)) {
      deg = ((deg % 360) + 360) % 360;
    }
    directionAngleDeg = deg;
  }

  const helpers = {
    imageSize: () => [imgW, imgH],
    pxPerMeter: ppm,
    looseBounds: true,
    looseBoundsMargin: imgH,
    append: true,
    getOrigin: ({ imageRect }) => {
      if (hasOrigin) {
        return { x: ox, y: oy };
      }
      const cx = imageRect ? imageRect.width * 0.5 : imgW * 0.5;
      const cy = imageRect ? imageRect.height * 0.5 : imgH * 0.5;
      return { x: cx, y: cy };
    },
    getTarget: () => {
      if (hasTarget) {
        return { x: tx, y: ty };
      }
      return null;
    },
    getDirectionAngleDeg: () => directionAngleDeg,
  };

  if (directionAngleDeg !== null) {
    helpers.directionAngleDeg = directionAngleDeg;
    helpers.directionDeg = directionAngleDeg;
    helpers.direction = directionAngleDeg;
  }
  if (hasOrigin) {
    helpers.origin = { x: ox, y: oy };
  }
  if (hasTarget) {
    helpers.target = { x: tx, y: ty };
  }

  const basePreset = spell.preset;
  const runtimePresetBase = buildRuntimePresetFromSpellPreset(
    basePreset,
    ppm
  );

  if (!runtimePresetBase) {
    return null;
  }

  let runtimePreset = runtimePresetBase;
  if (directionAngleDeg !== null) {
    try {
      const clone = deepClone(runtimePresetBase);
      applyAngleToPreset(clone, directionAngleDeg);
      runtimePreset = clone;
    } catch (e) {
      runtimePreset = runtimePresetBase;
    }
  }

  try {
    runtimePreset = applyCollisionContextToRuntimePreset(
      runtimePreset && runtimePreset !== runtimePresetBase ? runtimePreset : deepClone(runtimePreset),
      options.extra || null
    );
  } catch (e) {}

  const context = {
    spell,
    runtime,
    app,
    helpers,
    gridConfig,
    origin: hasOrigin ? { x: ox, y: oy } : null,
    target: hasTarget ? { x: tx, y: ty } : null,
    directionAngleDeg,
    runtimePreset,
    extra: options.extra || null,
  };

  if (typeof spell.run === "function") {
    return spell.run(context);
  }

  if (!runtimePreset || typeof runtime.runPreset !== "function") {
    return null;
  }

  const parent = app.particlesLayer || app.stage || null;
  if (!parent) {
    return null;
  }

  const instance = runtime.runPreset(runtimePreset, null, parent, parent, helpers);

  return instance;
}

let pendingSpell = null;

export function setPendingSpell(spellId, options = {}) {
  const spell = getSpell(spellId);
  if (!spell) {
    pendingSpell = null;
    return;
  }

  pendingSpell = {
    id: spellId,
    origin: options.origin || null,
    extra: options.extra || null,
    gridConfig: options.gridConfig || null,
  };

  try {
    if (pendingSpell.origin && typeof window !== "undefined") {
      window.__PA_SPELL_ORIGIN = {
        x: pendingSpell.origin.x,
        y: pendingSpell.origin.y,
      };
    }
  } catch {}
}

export function getPendingSpell() {
  return pendingSpell;
}

export function clearPendingSpell() {
  pendingSpell = null;
}

export function castPendingSpellAtTarget(target, options = {}) {
  if (!pendingSpell || !pendingSpell.id) {
    return null;
  }

  const mergedOptions = {
    origin: pendingSpell.origin || null,
    target: target || null,
    extra: pendingSpell.extra || null,
    gridConfig: pendingSpell.gridConfig || options.gridConfig || null,
    ...options,
  };

  const result = castSpellById(pendingSpell.id, mergedOptions);
  pendingSpell = null;
  return result;
}
